import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import config from '../../config/config';

export function ViewButcher() {
    const [butchers, setButchers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const loadButchers = useCallback(async () => {
        try {
            setLoading(true);
            const selectedYear = localStorage.getItem('qurbani_year') || new Date().getFullYear();
            const token = localStorage.getItem('token');
            const response = await fetch(`${config.baseUrl}/api/butcher/getbutcher?year=${selectedYear}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setButchers(Array.isArray(data) ? data : data.expenses || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadButchers(); }, [loadButchers]);

    const filteredButchers = butchers.filter(b =>
        b.butcher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.phone?.includes(searchTerm)
    );

    const totalPages = Math.ceil(filteredButchers.length / itemsPerPage);
    const currentItems = filteredButchers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            const token = localStorage.getItem('token');
            await fetch(`${config.baseUrl}/api/butcher/delete/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setButchers(prev => prev.filter(b => b.id !== id));
        } catch (err) { alert("Error deleting record."); }
    };

    return (
        <main className="p-2">
            <div className="max-w-6xl mx-auto space-y-6">

                {/* Header Section */}
                <div className="flex justify-between items-center px-2">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                            Butcher Registry
                        </h1>
                        <p className="text-sm text-slate-500 font-medium">Manage your service providers</p>
                    </div>
                    <Link to="/butcher/add" className="bg-blue-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95">
                        + Add Butcher
                    </Link>
                </div>

                {/* Search Card - ZABIHA Input Style */}
                <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200 p-6">
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-2 ml-1">Search Database</label>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Enter name or contact number..."
                            className="w-full px-5 py-4 border border-slate-200 rounded-2xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 sm:text-sm transition-all font-medium"
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Table Card - ZABIHA Main Container Style */}
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/60 border border-white overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-separate border-spacing-0">
                            <thead>
                                <tr className="bg-slate-900 text-white font-black uppercase text-[10px] tracking-[0.2em]">
                                    <th className="px-8 py-6">ID</th>
                                    <th className="px-8 py-6">Provider Details</th>
                                    <th className="px-8 py-6">Category</th>
                                    <th className="px-8 py-6 text-right">Advance Paid</th>
                                    <th className="px-8 py-6 text-right">Contract Amt</th>
                                    <th className="px-8 py-6 text-center">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {currentItems.length > 0 ? currentItems.map((butcher, index) => (
                                    <tr key={butcher.id} className="hover:bg-blue-50/30 transition-colors group">
                                        <td className="px-8 py-5 text-slate-400 font-mono text-xs">
                                            {((currentPage - 1) * itemsPerPage) + (index + 1)}
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="font-extrabold text-slate-900 uppercase text-xs tracking-tight">{butcher.butcher_name}</div>
                                            <div className="text-[11px] font-bold text-blue-600 mt-0.5">{butcher.phone}</div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <span className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase border border-slate-200">
                                                {butcher.cat_name}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-right font-bold text-slate-600 text-xs">
                                            Rs. {Number(butcher.deposit || 0).toLocaleString()}
                                        </td>
                                        <td className="px-8 py-5 text-right font-black text-blue-600 text-xs">
                                            Rs. {Number(butcher.ageed_amt || 0).toLocaleString()}
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex justify-center gap-4">
                                                <Link to={`/butcher/edit/${butcher.id}`} className="text-blue-600 font-black text-[10px] uppercase tracking-widest hover:underline decoration-2 underline-offset-4">
                                                    Edit
                                                </Link>
                                                <button onClick={() => handleDelete(butcher.id)} className="text-red-500 font-black text-[10px] uppercase tracking-widest hover:text-red-700 transition-colors">
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="px-8 py-20 text-center text-slate-400 font-bold uppercase text-xs tracking-widest italic">
                                            No Butcher Records Found in System
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="p-8 bg-slate-50/50 border-t border-slate-100 flex justify-between items-center">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.15em]">
                            Showing {currentItems.length} of {filteredButchers.length} Entries
                        </span>
                        <div className="flex gap-3">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="px-6 py-2.5 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase text-slate-600 disabled:opacity-30 shadow-sm hover:border-blue-300 transition-all"
                            >
                                Previous
                            </button>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="px-6 py-2.5 bg-white border border-slate-200 rounded-2xl text-[10px] font-black uppercase text-slate-600 disabled:opacity-30 shadow-sm hover:border-blue-300 transition-all"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </main>

    );
}